import React, { useEffect, useState, useCallback } from 'react';
import {
  User as FirebaseUser,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { User, UserRole, UserPermissions, Firm, FirmSettings, FirmSubscription, Admin, AdminPermissions } from '@/types';
import { getPermissionsForRole } from '@/utils/permissions';
import { getCurrentTimestamp } from '@/utils/dates';
import { logSuccessfulLogin, logLoginAttempt, logLogout } from '@/services/auditLogService';
import { logger } from '@/utils/logger';
import { mapFirebaseError } from '@/constants/errorMessages';
import { impersonationExpiryMillis, isImpersonationExpired } from '@/utils/impersonationExpiry';
import { AuthContext, AuthContextType, useAuth } from './AuthContext.definition';

// Re-export useAuth from definition file for backward compatibility
// eslint-disable-next-line react-refresh/only-export-components
export { useAuth };

/**
 * Max time logout() waits for the best-effort HIPAA logout audit before it signs
 * the user out anyway. createAuditLog is a callable Cloud Function; on a cold
 * start its response can take ~10s, which previously left logout hanging on a
 * spinner (QA finding B1-6). This cap keeps logout near-instant while still
 * giving a warm callable time to confirm — and a cold one time to *dispatch* its
 * request (with the still-valid token) before signOut revokes the credential.
 */
const LOGOUT_AUDIT_TIMEOUT_MS = 2500;

async function callEndImpersonation(sessionId: string | null, reason?: 'expired') {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const endImpersonationFn = httpsCallable(getFunctions(), 'endImpersonation');
  await endImpersonationFn({ sessionId, ...(reason ? { reason } : {}) });
}

/**
 * Close a session whose impersonationExpiresAt has passed. The rules already
 * refuse the grant, so this is cleanup — record the end server-side and drop
 * the stale claims from the token. Best-effort: the scheduled sweep
 * (expireImpersonationSessions) covers it if this call fails.
 */
async function endExpiredImpersonation(user: FirebaseUser, sessionId: string | null) {
  try {
    await callEndImpersonation(sessionId, 'expired');
    await user.getIdToken(true);
  } catch (error) {
    logger.warn('Could not end expired impersonation session; the scheduled sweep will', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [firmId, setFirmId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);

  // Admin state
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminProfile, setAdminProfile] = useState<Admin | null>(null);
  const [adminPermissions, setAdminPermissions] = useState<AdminPermissions | null>(null);

  // Impersonation state
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedFirmId, setImpersonatedFirmId] = useState<string | null>(null);
  const [impersonatedFirmName, setImpersonatedFirmName] = useState<string | null>(null);
  const [impersonationSessionId, setImpersonationSessionId] = useState<string | null>(null);
  const [impersonationExpiresAtMs, setImpersonationExpiresAtMs] = useState<number | null>(null);

  // Fetch admin profile from Firestore
  async function fetchAdminProfile(adminId: string): Promise<Admin | null> {
    try {
      const adminDocRef = doc(db, 'admins', adminId);
      const adminDoc = await getDoc(adminDocRef);

      if (adminDoc.exists()) {
        const adminData = adminDoc.data() as Admin;
        setAdminProfile(adminData);
        setAdminPermissions(adminData.permissions);
        setIsAdmin(true);
        return adminData;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching admin profile', error);
      return null;
    }
  }

  // Fetch user profile from Firestore
  const fetchUserProfile = useCallback(async (userId: string, forceRefresh = false) => {
    try {
      // Always resolve the profile for the *currently authenticated* Firebase user.
      // The firmId/role claims below are read from auth.currentUser, so the user-doc
      // id we read must come from that same source. Callers such as refreshUserProfile()
      // and refreshSession() pass a userId derived from React `currentUser` state, which
      // can lag auth.currentUser after an in-tab auth-user swap — e.g. a firm owner is
      // signed in, then an invitee signs in during /accept-invitation within the same
      // browser tab (shared Firebase Auth persistence). If we read
      // firms/{claimFirmId}/users/{stale-userId} we'd load a *different* member of the
      // same firm and leak their profile/PII (name, recovery email/phone, photo) into
      // the new user's session — the S17-1 onboarding prefill bug. Pinning both firmId
      // and the user id to auth.currentUser keeps the read self-consistent regardless of
      // stale caller state.
      const authUser = auth.currentUser;
      if (!authUser) {
        logger.warn('fetchUserProfile called with no authenticated user; clearing profile state');
        setUserProfile(null);
        setUserRole(null);
        setPermissions(null);
        setFirmId(null);
        return null;
      }

      if (authUser.uid !== userId) {
        logger.warn('fetchUserProfile userId does not match the authenticated user; using auth.currentUser', {
          requestedUserId: userId,
          authUserId: authUser.uid,
        });
      }
      const effectiveUserId = authUser.uid;

      // Get custom claims to find firmId and admin status
      const idTokenResult = await authUser.getIdTokenResult(forceRefresh);
      const claimIsAdmin = idTokenResult?.claims.isAdmin === true;
      const claimFirmId = idTokenResult?.claims.firmId as string | undefined;
      const claimImpersonating = idTokenResult?.claims.impersonating === true;
      const claimImpersonatedFirmId = idTokenResult?.claims.impersonatedFirmId as string | undefined;
      const claimSessionId = idTokenResult?.claims.impersonationSessionId as string | undefined;

      // Handle admin users
      if (claimIsAdmin) {
        setIsAdmin(true);
        await fetchAdminProfile(effectiveUserId);

        const impersonationExpired =
          claimImpersonating && isImpersonationExpired(idTokenResult?.claims ?? {});
        if (impersonationExpired) {
          logger.info('Impersonation session expired; ending it');
          await endExpiredImpersonation(authUser, claimSessionId || null);
        }

        // Handle impersonation state
        if (claimImpersonating && claimImpersonatedFirmId && !impersonationExpired) {
          setIsImpersonating(true);
          setImpersonatedFirmId(claimImpersonatedFirmId);
          setImpersonationSessionId(claimSessionId || null);
          setImpersonationExpiresAtMs(impersonationExpiryMillis(idTokenResult?.claims ?? {}));

          // Get firm name for display
          const firmDoc = await getDoc(doc(db, 'firms', claimImpersonatedFirmId));
          if (firmDoc.exists()) {
            setImpersonatedFirmName(firmDoc.data()?.firmName || 'Unknown Firm');
          }
        } else {
          setIsImpersonating(false);
          setImpersonatedFirmId(null);
          setImpersonatedFirmName(null);
          setImpersonationSessionId(null);
          setImpersonationExpiresAtMs(null);
        }

        // Clear regular user state for pure admin
        setUserProfile(null);
        setUserRole(null);
        setPermissions(null);
        setFirmId(null);
        return null;
      }

      // Handle regular users
      setIsAdmin(false);
      setAdminProfile(null);
      setAdminPermissions(null);
      setIsImpersonating(false);
      setImpersonatedFirmId(null);
      setImpersonatedFirmName(null);
      setImpersonationSessionId(null);
      setImpersonationExpiresAtMs(null);

      if (!claimFirmId) {
        logger.warn('No firmId found in custom claims - user may need to complete onboarding');
        // Clear any stale state
        setUserProfile(null);
        setUserRole(null);
        setPermissions(null);
        setFirmId(null);
        return null;
      }

      // Fetch user profile from Firestore
      const userDocRef = doc(db, `firms/${claimFirmId}/users`, effectiveUserId);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const userData = userDoc.data() as User;
        setUserProfile(userData);
        setUserRole(userData.role);
        setPermissions(getPermissionsForRole(userData.role));
        setFirmId(claimFirmId);

        // Self-heal a stale custom-claims token. Firestore rules authorize off the
        // token's `role` claim (request.auth.token.role → isAttorney()/isStaff()),
        // but custom-claims propagation after signup / invite-acceptance / role
        // change isn't synchronous with our getIdToken(true), so the cached session
        // token can lag the user doc: the UI reads role from the doc and looks
        // correct while writes still carry the stale claim, tripping attorney/staff
        // rule gates (B6-10). If the token role disagrees with the doc, force one
        // refresh so subsequent requests carry the right claim. Guarded on
        // !forceRefresh to avoid a refresh loop when the claim simply hasn't
        // propagated server-side yet (heals on a later fetch instead).
        const claimRole = idTokenResult?.claims.role as string | undefined;
        if (!forceRefresh && claimRole !== userData.role) {
          logger.warn('Custom-claims role lags user doc; forcing token refresh', {
            claimRole: claimRole ?? null,
            docRole: userData.role,
          });
          await auth.currentUser?.getIdToken(true);
        }
        return userData;
      } else {
        logger.error('User profile not found in Firestore');
        return null;
      }
    } catch (error) {
      logger.error('Error fetching user profile', error);
      // Clear state on error
      setUserProfile(null);
      setUserRole(null);
      setPermissions(null);
      setFirmId(null);
      return null;
    }
  }, []);

  // Signup function
  async function signup(
    email: string,
    password: string,
    displayName: string,
    firmName: string,
    role: UserRole,
    salutation: string = '',
    firstName: string = '',
    lastName: string = ''
  ) {
    setLoading(true);
    try {
      // Step 1: Generate firm ID (don't create document yet)
      const firmRef = doc(collection(db, 'firms'));
      const firmId = firmRef.id;

      // Step 2: Create user in Firebase Auth (so they're authenticated)
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update display name
      await updateProfile(user, { displayName });

      // Step 3: Create firm document (now user is authenticated and can pass Firestore rules)
      const defaultSettings: FirmSettings = {
        defaultRequestInterval: 30,
        ccParalegalEnabled: true,
        emailTemplate: {
          subject: 'Medical Records Request for {{clientName}}',
          body: `Dear {{providerName}},

We are writing to request copies of medical records for our client, {{clientName}}.

Please provide all medical records, including but not limited to:
- Office visit notes
- Test results
- Imaging reports
- Prescription records
- Treatment plans

Thank you for your prompt attention to this matter.`,
          signature: `Sincerely,\n${firmName}`,
        },
        firmEmail: email,
        firmPhone: '',
        firmAddress: '',
      };

      const defaultSubscription: FirmSubscription = {
        status: 'inactive',
        tier: 'standard',
        billingEmail: email,
        stripeCustomerId: '',
        stripeSubscriptionId: '',
        currentPeriodEnd: getCurrentTimestamp(),
        activeCasesCount: 0,
      };

      const newFirm: Firm = {
        firmId,
        firmName,
        ownerEmail: email,
        createdAt: getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp(),
        subscription: defaultSubscription,
        settings: defaultSettings,
        auditLog: true,
        dataRetentionDays: 2555, // ~7 years for HIPAA compliance
      };

      await setDoc(firmRef, newFirm);

      // Step 5: Create user profile in Firestore
      const userProfile: User = {
        userId: user.uid,
        email: user.email!,
        displayName,
        salutation: salutation as User['salutation'],
        firstName: firstName || displayName.split(' ')[0] || '',
        lastName: lastName || displayName.split(' ').slice(1).join(' ') || '',
        role,
        status: 'active',
        createdAt: getCurrentTimestamp(),
        updatedAt: getCurrentTimestamp(),
        lastLoginAt: getCurrentTimestamp(),
        createdBy: user.uid, // Self-created for first user
        ipAddress: '', // Will be set by Cloud Function
        isOwner: true, // First user is always the owner
      };

      const userDocRef = doc(db, `firms/${firmId}/users`, user.uid);
      await setDoc(userDocRef, userProfile);

      // Step 6: Set custom claims immediately so user can access the app
      // Call the Cloud Function to set firmId and role claims
      try {
        const { getFunctions, httpsCallable } = await import('firebase/functions');
        const functions = getFunctions();
        const setCustomClaims = httpsCallable(functions, 'setCustomClaimsImmediate');

        await setCustomClaims({ firmId, role });

        // Step 7: Force token refresh to get the new custom claims
        await user.getIdToken(true);

        logger.info('Custom claims set and token refreshed successfully');
      } catch (error: unknown) {
        logger.error('Error setting custom claims', error instanceof Error ? error : new Error(String(error)));
        // Don't fail signup if claims setting fails - the Firestore trigger will handle it as backup
        logger.warn('Custom claims will be set by Firestore trigger as fallback');
      }

      // Step 8: Seed system email templates for the new firm
      // This happens AFTER custom claims are set so user has firmId in their token
      try {
        const { seedSystemTemplates } = await import('@/services/emailTemplateService');
        await seedSystemTemplates(firmId, 'system', firmName);
        logger.info('Seeded system email templates for firm', { firmId });
      } catch (templateError) {
        // Don't fail signup if template seeding fails, but log it
        logger.error('Error seeding email templates during signup', { firmId, error: templateError });
      }

      // Step 9: Refresh user profile to populate context
      await fetchUserProfile(user.uid, true);

      // Step 8: Send email verification
      try {
        await sendEmailVerification(user, {
          url: `${import.meta.env.VITE_APP_URL || window.location.origin}/dashboard`,
          handleCodeInApp: false,
        });
        logger.info('Verification email sent successfully');
      } catch (verifyError: unknown) {
        // Don't fail signup if verification email fails - user can resend later
        logger.warn('Failed to send verification email, user can resend later', { error: verifyError instanceof Error ? verifyError.message : String(verifyError) });
      }
    } catch (error: unknown) {
      logger.error('Signup error', error instanceof Error ? error : new Error(String(error)));

      // Map Firebase errors to user-friendly messages using centralized mapping
      const errorMessage = mapFirebaseError(error);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  // Login function
  async function login(email: string, password: string) {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Force token refresh to ensure we have the latest custom claims
      // This is critical for Firebase security rules that depend on firmId/role claims
      await userCredential.user.getIdToken(true);

      // Get token to check if admin (with freshly refreshed claims)
      const idTokenResult = await userCredential.user.getIdTokenResult();
      const claimIsAdmin = idTokenResult.claims.isAdmin === true;

      if (claimIsAdmin) {
        // Admin login - fetch admin profile and set admin claims
        await fetchAdminProfile(userCredential.user.uid);

        // Call Cloud Function to update admin last login and audit log
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const functions = getFunctions();
          const setAdminClaims = httpsCallable(functions, 'setAdminClaims');
          await setAdminClaims({ adminId: userCredential.user.uid });
        } catch (error) {
          logger.warn('Failed to call setAdminClaims', { error: error instanceof Error ? error.message : String(error) });
        }

        logger.info('Admin login successful');
      } else {
        // Regular user login - fetch profile with forced token refresh
        const userData = await fetchUserProfile(userCredential.user.uid, true);

        // Check if user is deactivated
        if (userData && userData.status === 'inactive') {
          // Sign out the user immediately
          await signOut(auth);
          throw new Error('Your account has been deactivated. Please contact your firm administrator.');
        }

        // Get firmId from custom claims directly (freshly refreshed)
        const userFirmId = idTokenResult.claims.firmId as string | undefined;

        if (userFirmId && userData) {
          const userDocRef = doc(db, `firms/${userFirmId}/users`, userCredential.user.uid);
          await setDoc(
            userDocRef,
            { lastLoginAt: getCurrentTimestamp() },
            { merge: true }
          );

          // HIPAA Audit: Log successful login (using firmId from claims, not state)
          await logSuccessfulLogin(userFirmId, userCredential.user.uid, email);
        } else if (!userFirmId) {
          // Log diagnostic info when firmId is missing
          logger.warn('Login successful but firmId claim is missing. User may need to refresh session.', {
            userId: userCredential.user.uid,
            email,
            claims: Object.keys(idTokenResult.claims),
          });
        }
      }
    } catch (error: unknown) {
      logger.error('Login error', error instanceof Error ? error : new Error(String(error)));

      // If it's our custom deactivation error, re-throw as-is
      const errMessage = error instanceof Error ? error.message : String(error);
      if (errMessage?.includes('deactivated')) {
        throw error;
      }

      // Map Firebase errors to user-friendly messages using centralized mapping
      const errorMessage = mapFirebaseError(error);

      // HIPAA Audit: Log failed login attempt
      // This uses a special unauthenticated Cloud Function
      await logLoginAttempt(email, false, errorMessage);

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  // Logout function
  async function logout() {
    setLoading(true);
    try {
      // If impersonating, end impersonation first
      if (isImpersonating) {
        await stopImpersonation();
      }

      // HIPAA Audit: record the logout event — but never let it hold the user on
      // a logout spinner (QA B1-6). createAuditLog is a callable Cloud Function
      // whose *response* can take ~10s on a cold start. Crucially, the signOut()
      // below tears down the credential that callable authenticates with, so we
      // must let logLogout() DISPATCH its request — which attaches the still-valid
      // ID token and happens within milliseconds — BEFORE we sign out. We just
      // don't wait for the slow cold-start *response*: race the audit against a
      // short timeout. A warm callable resolves well within the window (audit
      // confirmed); a cold one is left in-flight with its token already attached,
      // so the audit still lands server-side. logLogout is best-effort and never
      // throws (logAudit wraps it with silent:true, rethrow:false).
      if (firmId && currentUser && userProfile && !isAdmin) {
        let auditTimer: ReturnType<typeof setTimeout> | undefined;
        try {
          await Promise.race([
            logLogout(firmId, currentUser.uid, userProfile.email),
            new Promise<void>((resolve) => {
              auditTimer = setTimeout(resolve, LOGOUT_AUDIT_TIMEOUT_MS);
            }),
          ]);
        } finally {
          if (auditTimer) clearTimeout(auditTimer);
        }
      }

      // NOTE: signOut MUST stay after the audit dispatch above — it revokes the
      // credential createAuditLog needs. Do not reorder these two steps.
      await signOut(auth);

      // Clear all state
      setCurrentUser(null);
      setUserProfile(null);
      setUserRole(null);
      setPermissions(null);
      setFirmId(null);
      setIsAdmin(false);
      setAdminProfile(null);
      setAdminPermissions(null);
      setIsImpersonating(false);
      setImpersonatedFirmId(null);
      setImpersonatedFirmName(null);
      setImpersonationSessionId(null);
      setImpersonationExpiresAtMs(null);
    } catch (error: unknown) {
      logger.error('Logout error', error instanceof Error ? error : new Error(String(error)));
      throw new Error('Failed to log out');
    } finally {
      setLoading(false);
    }
  }

  // Reset password function
  async function resetPassword(email: string) {
    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: unknown) {
      logger.error('Password reset error', error instanceof Error ? error : new Error(String(error)));

      // Map Firebase errors to user-friendly messages using centralized mapping
      const errorMessage = mapFirebaseError(error);

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  // Send email verification
  // Send email verification
  async function sendVerificationEmail() {
    if (!currentUser) {
      throw new Error('No user logged in');
    }

    setLoading(true);
    try {
      // Use the custom Cloud Function to send the verification email via Emailit
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');

      await sendCustomVerificationEmail();
      logger.info('Verification email sent successfully');
    } catch (error: unknown) {
      logger.error('Email verification error', error instanceof Error ? error : new Error(String(error)));

      // Map Firebase errors to user-friendly messages using centralized mapping
      const errorMessage = mapFirebaseError(error);

      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  // Update user profile (display name)
  async function updateUserProfile(displayName: string) {
    if (!currentUser || !firmId) {
      throw new Error('No user logged in');
    }

    setLoading(true);
    try {
      // Update Firebase Auth profile
      await updateProfile(currentUser, { displayName });

      // Update Firestore profile
      const userDocRef = doc(db, `firms/${firmId}/users`, currentUser.uid);
      await setDoc(
        userDocRef,
        {
          displayName,
          updatedAt: getCurrentTimestamp(),
        },
        { merge: true }
      );

      // Refresh user profile
      await fetchUserProfile(currentUser.uid);
    } catch (error: unknown) {
      logger.error('Profile update error', error instanceof Error ? error : new Error(String(error)));
      throw new Error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  }

  // Refresh user profile from Firestore
  async function refreshUserProfile(forceRefresh = false) {
    if (currentUser) {
      // Force token refresh to get latest custom claims
      if (forceRefresh) {
        await currentUser.getIdToken(true);
      }
      await fetchUserProfile(currentUser.uid, forceRefresh);
    }
  }

  // Force session refresh - useful when permission errors occur
  async function refreshSession() {
    if (currentUser) {
      setLoading(true);
      try {
        // Force token refresh to ensure custom claims are loaded
        await currentUser.getIdToken(true);
        logger.info('Session refreshed with forced token refresh');

        // Re-fetch user profile with forced refresh
        await fetchUserProfile(currentUser.uid, true);
        logger.info('User profile refreshed after session refresh');

        return true;
      } catch (error: unknown) {
        logger.error('Failed to refresh session', error instanceof Error ? error : new Error(String(error)));
        throw new Error('Failed to refresh session. Please try logging out and back in.');
      } finally {
        setLoading(false);
      }
    }
    throw new Error('No authenticated user found');
  }

  // Start impersonation (admin only)
  async function startImpersonation(targetFirmId: string) {
    if (!isAdmin || !currentUser) {
      throw new Error('Only admins can impersonate');
    }

    setLoading(true);
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const startImpersonationFn = httpsCallable(functions, 'startImpersonation');

      const result = await startImpersonationFn({ firmId: targetFirmId });
      const data = result.data as { success: boolean; sessionId: string; firmName: string };

      if (data.success) {
        // Force token refresh to get new impersonation claims
        const tokenResult = await currentUser.getIdTokenResult(true);

        // Update local state
        setIsImpersonating(true);
        setImpersonatedFirmId(targetFirmId);
        setImpersonatedFirmName(data.firmName);
        setImpersonationSessionId(data.sessionId);
        setImpersonationExpiresAtMs(impersonationExpiryMillis(tokenResult.claims));

        logger.info(`Started impersonating firm: ${data.firmName}`);
      }
    } catch (error: unknown) {
      logger.error('Error starting impersonation', error instanceof Error ? error : new Error(String(error)));
      const errMessage = error instanceof Error ? error.message : String(error);
      throw new Error(errMessage || 'Failed to start impersonation');
    } finally {
      setLoading(false);
    }
  }

  // Stop impersonation (admin only)
  async function stopImpersonation() {
    if (!isAdmin || !currentUser) {
      throw new Error('Only admins can stop impersonation');
    }

    setLoading(true);
    try {
      await callEndImpersonation(impersonationSessionId);

      // Force token refresh to clear impersonation claims
      await currentUser.getIdToken(true);

      // Clear impersonation state
      setIsImpersonating(false);
      setImpersonatedFirmId(null);
      setImpersonatedFirmName(null);
      setImpersonationSessionId(null);
      setImpersonationExpiresAtMs(null);

      logger.info('Stopped impersonation');
    } catch (error: unknown) {
      logger.error('Error stopping impersonation', error instanceof Error ? error : new Error(String(error)));
      const errMessage = error instanceof Error ? error.message : String(error);
      throw new Error(errMessage || 'Failed to stop impersonation');
    } finally {
      setLoading(false);
    }
  }

  // End impersonation when the session's expiry passes. The rules stop
  // honouring the grant at that instant, so leaving the admin on the firm view
  // would only produce failing reads.
  useEffect(() => {
    if (!isImpersonating || impersonationExpiresAtMs === null || !currentUser) return;

    const timer = setTimeout(() => {
      logger.info('Impersonation session expired; ending it');
      void endExpiredImpersonation(currentUser, impersonationSessionId).finally(() => {
        setIsImpersonating(false);
        setImpersonatedFirmId(null);
        setImpersonatedFirmName(null);
        setImpersonationSessionId(null);
        setImpersonationExpiresAtMs(null);
      });
    }, Math.max(0, impersonationExpiresAtMs - Date.now()));

    return () => clearTimeout(timer);
  }, [isImpersonating, impersonationExpiresAtMs, currentUser, impersonationSessionId]);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        // Clear all state on logout
        setUserProfile(null);
        setUserRole(null);
        setPermissions(null);
        setFirmId(null);
        setIsAdmin(false);
        setAdminProfile(null);
        setAdminPermissions(null);
        setIsImpersonating(false);
        setImpersonatedFirmId(null);
        setImpersonatedFirmName(null);
        setImpersonationSessionId(null);
        setImpersonationExpiresAtMs(null);
      }

      setInitializing(false);
    });

    return unsubscribe;
  }, [fetchUserProfile]);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    user: userProfile, // alias for compatibility
    userRole,
    permissions,
    firmId,
    effectiveFirmId: isImpersonating ? impersonatedFirmId : firmId,
    isAdmin,
    adminProfile,
    adminPermissions,
    isImpersonating,
    impersonatedFirmId,
    impersonatedFirmName,
    impersonationSessionId,
    loading,
    initializing,
    signup,
    login,
    logout,
    resetPassword,
    sendVerificationEmail,
    updateUserProfile,
    refreshUserProfile,
    refreshSession,
    startImpersonation,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
